import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

const RECEIPTS: Array<{ value: string; label: string }> = [
  { value: "880K", label: "lines of code" },
  { value: "15,749", label: "unit tests" },
  { value: "10", label: "proprietary ASO engines" },
  { value: "45+", label: "backend services" },
  { value: "22+", label: "external integrations" },
  { value: "5", label: "customer-facing surfaces" },
];

export default function CapitalEfficiency() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix C · The AI Arbitrage">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          Building Apptonomy pre-AI-coding (e.g. 2022) would have cost{" "}
          <span className="text-blue-700">$20M+.</span>
        </h1>
        <p className="mt-3 text-lg font-medium italic tracking-tight text-slate-700 md:text-xl">
          AI-native development is the arbitrage — same platform, two price
          tags.
        </p>
      </SlideHeader>

      {/* The confrontation — one build, two eras */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            2022 — conventional build{" "}
            <span className="normal-case italic tracking-normal text-slate-400">
              (pre-GPT-4)
            </span>
          </p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900 md:text-4xl">
            $22–25M
          </p>
          <p className="mt-0.5 text-sm font-semibold text-slate-700 md:text-base">
            raised · 26 FTE · 24 months to production
          </p>
          <p className="mt-2 text-sm leading-snug text-slate-600 md:text-base">
            $14.3M burn to GA · $2.5–3.5M for the ML pod alone (no LLM APIs —
            bespoke NLP) · $8–9M post-GA runway.
          </p>
          <p className="mt-2 text-[11px] italic text-slate-400">
            Per internal build-cost analysis, 2026-06-05.
          </p>
        </div>
        <div className="rounded-xl bg-blue-50 p-4 ring-2 ring-blue-400 md:p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700">
            2026 — the AI-native build
          </p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-blue-700 md:text-4xl">
            2 founders + AI
          </p>
          <p className="mt-0.5 text-sm font-semibold text-slate-700 md:text-base">
            in production · paying customers
          </p>
          <p className="mt-2 text-sm leading-snug text-slate-700 md:text-base">
            Shipped nightly by an autonomous deploy pipeline · engineering at
            ~9% of the 24-month operating plan.
            <SupLink to="ai-native-factory">F</SupLink>
          </p>
        </div>
      </div>

      {/* The receipts — same platform either way */}
      <div className="mt-4 md:mt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          The platform both price tags buy
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-6 md:gap-3">
          {RECEIPTS.map((r) => (
            <div
              key={r.label}
              className="rounded-lg bg-slate-50 p-2.5 ring-1 ring-slate-200 md:p-3"
            >
              <p className="text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                {r.value}
              </p>
              <p className="mt-0.5 text-xs leading-snug text-slate-600">
                {r.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Why the gap holds */}
      <div className="mt-4 md:mt-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          Why the gap holds
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3">
          <Beat title="AI-native by design">
            Frontier models and agentic workflows woven through every engine,
            every workflow, every release — not bolted on.
          </Beat>
          <Beat title="Incumbents can't pivot">
            AppTweak, Sensor Tower — legacy codebases. Adopting these patterns
            is a multi-year rebuild against customers paying for the old
            dashboard.
          </Beat>
          <Beat title="New entrants can't ship the system">
            Thin wrappers on a single frontier model — no production
            engineering DNA, no integrations, no orchestration.
          </Beat>
        </div>
        <p className="mt-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
          20+ years at production scale × AI-native execution
        </p>
      </div>
    </div>
  );
}

function Beat({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200">
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-900">
        {title}
      </p>
      <p className="mt-1 text-xs leading-snug text-slate-600 md:text-sm">
        {children}
      </p>
    </div>
  );
}
